import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { getImpersonationTargetLabel } from "@components/impersonation/impersonation-helper";
import { BaseMemberIdProps } from "@custom-types/base-types";
import { Button, useToast } from "@flat-ui";
import { runtimeConfig } from "@src/runtime-config";
import { addNumberedEnv } from "@utils/url-utils";
import { copyPendingTextToClipboard } from "@utils/clipboard-utils";
import { useState } from "react";

interface Props extends BaseMemberIdProps {
  /** The selected numbered environment (e.g. "dev15") to redirect to after login */
  numberedEnv: string;
}

/**
 * Button that generates a user login access link and copies it to the clipboard
 * It accepts numbered environment as well
 */
export function CopyImpersonationButton({
  memberId,
  numberedEnv,
}: Props): React.JSX.Element {
  const { handleErrorWithToast } = useErrorHandler();
  const [isLoading, setIsLoading] = useState(false);
  const { openToast } = useToast();

  async function onLoginLinkRequest(): Promise<void> {
    setIsLoading(true);
    try {
      const redirectUrl = addNumberedEnv(
        runtimeConfig.urls.dashboardUrl,
        numberedEnv
      );
      // The clipboard write has to start before any await, see copyPendingTextToClipboard
      await copyPendingTextToClipboard(
        getImpersonationTargetLabel("user", memberId, redirectUrl)
      );
      openToast({
        title: "Login link copied to clipboard",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `testing-impersonation-login-link-${Date.now()}`,
        title: "Could not retrieve the user login access link",
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Button
      variant="secondary"
      size="s"
      isLoading={isLoading}
      onClick={(event) => {
        event.stopPropagation();
        void onLoginLinkRequest();
      }}
    >
      Copy
    </Button>
  );
}
